// src/marketing/launch/hooks/useLaunchTasks.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import type { LaunchTask } from '../../../lib/types'

const STALE = 1000 * 60 * 2

const launchTaskKeys = {
  all: ['launch-tasks'] as const,
  list: (planId: string) => ['launch-tasks', 'list', planId] as const,
  detail: (id: string) => ['launch-tasks', 'detail', id] as const,
}

export function useLaunchTasks(planId: string | null) {
  return useQuery({
    queryKey: planId ? launchTaskKeys.list(planId) : launchTaskKeys.all,
    queryFn: () =>
      api.get<{ tasks: LaunchTask[] }>(`/api/launch-tasks?planId=${planId}`).then((r) => r.tasks),
    enabled: planId !== null,
    staleTime: STALE,
  })
}

type PatchInput = {
  title?: string
  detail?: string | null
  dayOffset?: number | null
  sortOrder?: number
  milestoneOrder?: number
  done?: boolean
}

export function usePatchLaunchTask(planId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PatchInput }) =>
      api.patch<{ task: LaunchTask }>(`/api/launch-tasks/${id}`, data),
    onMutate: async ({ id, data }) => {
      if (!planId) return
      const key = launchTaskKeys.list(planId)
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<LaunchTask[]>(key)
      qc.setQueryData<LaunchTask[]>(key, (old) =>
        (old ?? []).map((t) => (t.id === id ? { ...t, ...data } : t)),
      )
      return { previous, key }
    },
    onError: (_err, _vars, context) => {
      if (context?.key && context.previous) {
        qc.setQueryData(context.key, context.previous)
      }
    },
    onSettled: (_data, _err, _vars, context) => {
      const key = context?.key ?? (planId ? launchTaskKeys.list(planId) : undefined)
      if (key) qc.invalidateQueries({ queryKey: key })
    },
  })
}

type CreateTaskInput = {
  planId: string
  milestone: string
  milestoneOrder: number
  title: string
  detail: string | null
  dayOffset: number | null
  sortOrder: number
}

export function useCreateLaunchTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTaskInput) =>
      api.post<{ task: LaunchTask }>('/api/launch-tasks', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: launchTaskKeys.all })
    },
  })
}

export function useDeleteLaunchTask(planId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<{ success: boolean }>(`/api/launch-tasks/${id}`),
    onSuccess: () => {
      if (planId) qc.invalidateQueries({ queryKey: launchTaskKeys.list(planId) })
      else qc.invalidateQueries({ queryKey: launchTaskKeys.all })
    },
  })
}
